import React from 'react';
import PropTypes from 'prop-types';

import withElastic from '../data/with-elastic';
import queryBuilder from '../../query';

const RATING_INTERVAL_SIZE = 2;

const createRatingFilter = (field, selectedCategories, selectedStores) => ({
  filter: {
    bool: {
      must: [
        {
          range: {
            [field]: {
              gt: 1,
            },
          },
        },
        selectedCategories.length && {
          terms: {
            'product.category.path.id': selectedCategories,
          },
        },
        selectedStores.length && {
          terms: {
            'store.id': selectedStores,
          },
        },
      ].filter(x => x),
    },
  },
  aggs: {
    bars: {
      histogram: {
        field,
        interval: RATING_INTERVAL_SIZE,
        extended_bounds: {
          min: 0,
          max: 10,
        },
      },
    },
  },
});

const aggregationFields = ({
  visibleCategories,
  selectedCategories,
  selectedStores,
  maxStores,
}) => ({
  stores: {
    filter: {
      bool: {
        must: [
          selectedCategories.length && {
            terms: {
              'product.category.path.id': selectedCategories,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      current: {
        terms: { field: 'store.id', size: maxStores },
        aggs: {
          top_hits: {
            top_hits: {
              _source: ['store.id', 'store.name'],
              size: 1,
            },
          },
        },
      },
    },
  },
  category: {
    filter: {
      bool: {
        must: [
          { terms: { 'product.category.path.id': visibleCategories } },
          selectedStores.length && {
            terms: {
              'store.id': selectedStores,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      current: {
        terms: {
          field: 'product.category.path.id',
          include: visibleCategories,
          size: visibleCategories.length,
        },
      },
    },
  },

  prices: {
    filter: {
      bool: {
        must: [
          selectedCategories.length && {
            terms: {
              'product.category.path.id': selectedCategories,
            },
          },
          selectedStores.length && {
            terms: {
              'store.id': selectedStores,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      max_price: {
        max: {
          field: 'price.offer',
        },
      },
      min_price: {
        min: {
          field: 'price.offer',
        },
      },
    },
  },

  store_rating: createRatingFilter(
    'store.user_reviews.rating',
    selectedCategories,
    selectedStores
  ),
  product_rating: createRatingFilter(
    'product.user_reviews.rating',
    selectedCategories,
    selectedStores
  ),
});

const getVisibleCategories = (tree, expanded) => {
  const selected = [];

  const pusher = child => {
    selected.push(child.id);

    if (child.children && expanded.includes(child.id)) {
      child.children.forEach(pusher);
    }
  };

  tree.forEach(pusher);

  return selected;
};

const buildQuery = (state, props) => {
  const { query } = props.router;

  const levelOne = getVisibleCategories(
    props.categoryTree,
    props.expandedCategories
  );

  const aggs = aggregationFields({
    selectedCategories: props.router.categories,
    visibleCategories: getVisibleCategories(props.categoryTree, levelOne),
    selectedStores: props.router.stores,
    maxStores: props.router.maxStores,
  });

  const body = {
    size: 0,
    aggs,
    query: {
      bool: {
        must: [],
      },
    },
  };

  if (props.experimentFilter) {
    body.query.bool.must.push(props.experimentFilter);
  }

  if (query) {
    body.query.bool.must.push(queryBuilder(query));
  }

  return body;
};

class Aggregations extends React.Component {
  shouldComponentUpdate(nextProps) {
    const expandedChanged =
      this.props.expandedCategories.length !==
      nextProps.expandedCategories.length;
    const routerChanged = this.props.router !== nextProps.router;
    const dataChanged = this.props.data !== nextProps.data;
    return routerChanged || expandedChanged || dataChanged;
  }

  render() {
    const { data, errors } = this.props;
    return this.props.render({ data, errors });
  }
}

Aggregations.propTypes = {
  data: PropTypes.object,
  errors: PropTypes.array,
  render: PropTypes.func.isRequired,
  router: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  expandedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
};

Aggregations.defaultProps = {
  data: null,
  errors: null,
};

export default withElastic(buildQuery, 1500)(Aggregations);
